import { useEffect, useRef, useState, type ReactNode } from "react";
import { ArrowsDownUp, CaretDown, Check, SquaresFour } from "@phosphor-icons/react";
import type { ThreadSort } from "../../shared/threadSorting.ts";

export type { ThreadSort } from "../../shared/threadSorting.ts";

/** Below this viewport width the field labels ("Sort", "Group") don't fit on
    the one-line toolbar; they hide and the popover gains a heading instead so
    the context still travels into the open state. */
const COMPACT_QUERY = "(max-width: 500px)";

function useCompactControls(): boolean {
  const [compact, setCompact] = useState(() => window.matchMedia(COMPACT_QUERY).matches);
  useEffect(() => {
    const query = window.matchMedia(COMPACT_QUERY);
    const onChange = (event: MediaQueryListEvent) => setCompact(event.matches);
    query.addEventListener("change", onChange);
    return () => query.removeEventListener("change", onChange);
  }, []);
  return compact;
}

function PopoverSelect<V extends string>({ label, heading, icon, value, options, onChange, compact, testid }: {
  label: string;
  heading: string;
  icon: ReactNode;
  value: V;
  options: ReadonlyArray<{ value: V; label: string }>;
  onChange: (value: V) => void;
  compact: boolean;
  testid: string;
}) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const selected = options.find((option) => option.value === value) ?? options[0];

  function close(refocusTrigger: boolean) {
    setOpen(false);
    if (refocusTrigger) triggerRef.current?.focus();
  }

  // Move focus onto the current choice so Escape/arrow keys work immediately.
  useEffect(() => {
    if (!open) return;
    const current = popoverRef.current?.querySelector<HTMLButtonElement>('[aria-selected="true"]');
    (current ?? popoverRef.current?.querySelector("button"))?.focus();
  }, [open]);

  function onPopoverKeyDown(event: React.KeyboardEvent) {
    if (event.key === "Escape") {
      event.stopPropagation();
      close(true);
      return;
    }
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      const items = Array.from(popoverRef.current?.querySelectorAll<HTMLButtonElement>(".select-option") ?? []);
      const index = items.indexOf(document.activeElement as HTMLButtonElement);
      const next = event.key === "ArrowDown" ? Math.min(items.length - 1, index + 1) : Math.max(0, index - 1);
      items[next]?.focus();
    }
  }

  return (
    <div className="select-field">
      {!compact && <span className="select-field-label" aria-hidden="true">{label}</span>}
      <button
        type="button"
        ref={triggerRef}
        className={`select-pill${open ? " is-open" : ""}`}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`${heading}: ${selected.label}`}
        data-testid={`${testid}-trigger`}
        onClick={() => setOpen((current) => !current)}
      >
        {icon}
        <span className="select-pill-value">{selected.label}</span>
        <CaretDown weight="bold" className="select-pill-caret" aria-hidden="true" />
      </button>
      {open && (
        <>
          {/* The scrim makes outside-tap an explicit dismiss without also
              activating whatever sits underneath. Dismiss on click (not
              pointerdown) so a second tap on the trigger closes without the
              trailing click event re-opening the popover. */}
          <div className="select-scrim" onClick={() => close(false)} />
          <div
            className="select-popover"
            role="listbox"
            aria-label={heading}
            data-testid={`${testid}-popover`}
            ref={popoverRef}
            onKeyDown={onPopoverKeyDown}
          >
            {compact && <div className="select-popover-heading">{heading}</div>}
            {options.map((option) => (
              <button
                key={option.value}
                type="button"
                role="option"
                aria-selected={option.value === value}
                className="select-option"
                data-testid={`${testid}-option-${option.value}`}
                onClick={() => {
                  onChange(option.value);
                  close(true);
                }}
              >
                <span>{option.label}</span>
                {option.value === value && <Check weight="bold" aria-hidden="true" />}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/** Sort/group controls: two labeled popover selects that stay on one toolbar
    line at every width. `postNoun` controls audience-facing wording — the
    participant surface never says "thread". */
export function SortControls({ sort, onSort, byCategory, onByCategory, postNoun = "thread" }: {
  sort: ThreadSort;
  onSort: (s: ThreadSort) => void;
  byCategory: boolean;
  onByCategory: (v: boolean) => void;
  postNoun?: "thread" | "post";
}) {
  const compact = useCompactControls();
  return (
    <>
      <PopoverSelect
        label="Sort"
        heading="Sort by"
        icon={<ArrowsDownUp weight="bold" aria-hidden="true" />}
        value={sort}
        options={[
          { value: "latest-thread", label: postNoun === "post" ? "Latest post" : "Latest thread" },
          { value: "oldest-thread", label: postNoun === "post" ? "Oldest post" : "Oldest thread" },
          { value: "latest-reply", label: "Latest reply" },
          { value: "most-upvotes", label: "Most upvotes" },
        ]}
        onChange={onSort}
        compact={compact}
        testid="sort-select"
      />
      <PopoverSelect
        label="Group"
        heading="Group by"
        icon={<SquaresFour weight="bold" aria-hidden="true" />}
        value={byCategory ? "category" : "none"}
        options={[
          { value: "none", label: "None" },
          { value: "category", label: "By category" },
        ]}
        onChange={(value) => onByCategory(value === "category")}
        compact={compact}
        testid="group-select"
      />
    </>
  );
}
